// PR-35: read-only Documents on mobile.
//
// Mobile users can browse generated long-form artifacts (design docs,
// pitch decks, blog posts, outlines) but can't trigger a regenerate or
// create a new one — both are heavy ops kept on desktop per the M1
// product decision. The list shows what's there + lets the user open
// any saved doc to read it.
//
// Pattern: full-screen modal with a left-rail list when no doc is
// selected; tap a doc → switch to a viewer with a "← back" button.

import { useEffect, useState } from 'react'
import { api, type DocumentRecord } from '../../api'

interface Props {
  open: boolean
  projectId: string | null
  onClose: () => void
}

export function MobileDocumentsModal({ open, projectId, onClose }: Props) {
  const [docs, setDocs] = useState<DocumentRecord[] | null>(null)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [active, setActive] = useState<DocumentRecord | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) return
    if (!projectId) return
    let cancelled = false
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true)
    setError(null)
    api.listDocuments(projectId)
      .then((rs) => {
        if (cancelled) return
        // Newest first matches the desktop default.
        setDocs(rs.sort((a, b) => b.generated_at.localeCompare(a.generated_at)))
      })
      .catch((e) => {
        if (cancelled) return
        setError(`load documents: ${(e as Error).message}`)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, projectId])

  // Lazy-load the active doc body when the user picks one. The list
  // response is full markdown today (see DocumentRecord), so we could
  // use the row directly — but going through getDocument futureproofs
  // for a paginated list shape. Cheap either way.
  useEffect(() => {
    if (!activeId || !projectId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setActive(null)
      return
    }
    let cancelled = false
    api.getDocument(projectId, activeId)
      .then((d) => {
        if (!cancelled) setActive(d)
      })
      .catch((e) => {
        if (!cancelled) setError(`load document: ${(e as Error).message}`)
      })
    return () => {
      cancelled = true
    }
  }, [activeId, projectId])

  useEffect(() => {
    if (!open) return
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        if (activeId) setActiveId(null)
        else onClose()
      }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [open, activeId, onClose])

  if (!open) return null

  return (
    <div className="mobile-modal" role="dialog" aria-label="Documents">
      <header className="mobile-modal__head">
        {activeId ? (
          <>
            <button
              type="button"
              className="mobile-peek__close"
              onClick={() => setActiveId(null)}
              aria-label="Back"
            >
              ‹
            </button>
            <span className="mobile-modal__title">
              {active?.title ?? 'Document'}
            </span>
          </>
        ) : (
          <>
            <button
              type="button"
              className="mobile-peek__close"
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </button>
            <span className="mobile-modal__title">Documents</span>
          </>
        )}
      </header>

      {error && (
        <div className="mobile-toast mobile-toast--error" role="alert">
          {error}
        </div>
      )}

      {!activeId ? (
        loading && !docs ? (
          <div className="mobile-distill__progress mono">loading…</div>
        ) : !docs || docs.length === 0 ? (
          <div className="mobile-distill__empty">
            No documents yet. Open desktop to generate.
          </div>
        ) : (
          <ul className="mobile-docs__list">
            {docs.map((d) => (
              <li key={d.id}>
                <button
                  type="button"
                  className="mobile-docs__row"
                  onClick={() => setActiveId(d.id)}
                >
                  <div className="mobile-docs__row-template mono">
                    {d.template}
                  </div>
                  <h3 className="mobile-docs__row-title">{d.title}</h3>
                  <div className="mobile-docs__row-date mono">
                    {formatDate(d.generated_at)}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )
      ) : !active ? (
        <div className="mobile-distill__progress mono">loading…</div>
      ) : (
        <div className="mobile-docs__viewer">
          <pre className="mobile-docs__markdown">{active.markdown}</pre>
        </div>
      )}
    </div>
  )
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toISOString().slice(0, 16).replace('T', ' ')
  } catch {
    return iso
  }
}
